import getAllFiltered from "@api/graphql/generic/get-all-filtered";
import { useUserContext } from "@contexts/UserContext/index.context";
import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { PaginationState } from "@tanstack/react-table";
import type { GetAllInput, OrderType, WhereFilter } from "@types_def/common/filter.types";
import type { QueryType } from "@types_def/common/query.types";
import type { Delivery } from "@types_def/models/delivery.types";
import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { DataTable } from "@/components/ui/data-table";
import { useDataTable } from "@/hooks/use-data-table";
import { toDeliveryListSortField } from "@/utils/delivery-list-sort-field";

import { SUBSCRIPTION_PAYMENT_COLUMNS } from "./columns";

type TableState = {
  filter?: WhereFilter<Delivery>;
  deliveriesTableQueryFields: QueryType<Delivery> | undefined;
};

function SubscriptionInvoicesTable() {
  "use no memo";
  const [searchParams, setSearchParams] = useSearchParams();

  const pageIndex = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;
  const orderByRaw = searchParams.get("orderBy");
  const orderBy =
    orderByRaw !== null && orderByRaw !== ""
      ? (toDeliveryListSortField(orderByRaw) as keyof Delivery)
      : "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";

  const location = useLocation();
  const urlRole = (
    location.pathname.match(/^\/([^/]+)/)?.[1] !== undefined
      ? location.pathname.match(/^\/([^/]+)/)?.[1]
      : "client"
  ) as "admin" | "client";
  const organizationId = useUserContext().userState?.organizationId;

  const tableState: TableState = useMemo(() => {
    const currentDeliveriesTableQueryFields: QueryType<Delivery> = {
      id: true,
      publicId: true,
      type: true,
      accepted_offer: {
        demandedPrice: true,
        driver: {
          firstName: true,
          lastName: true,
        },
      },
      deliveryOrganization: {
        name: true,
      },
      completedAt: true,
      invoice: true,
    };

    if (urlRole === "admin") {
      return {
        filter: {
          invoice: {
            not: null,
          },
        } as unknown as WhereFilter<Delivery>,
        deliveriesTableQueryFields: currentDeliveriesTableQueryFields,
      };
    }

    return {
      filter: {
        invoice: {
          not: null,
        },
        organization: {
          slug: organizationId,
        },
      } as unknown as WhereFilter<Delivery>,
      deliveriesTableQueryFields: currentDeliveriesTableQueryFields,
    };
  }, [urlRole, organizationId]);
  const queryInput = useMemo<GetAllInput<Delivery, keyof Delivery>>(
    () => ({
      keyword: "",
      pagination: { page: pageIndex, perPage: pageSize },
      sort: { field: orderBy, order: order as OrderType },
      filter: tableState.filter,
    }),
    [pageIndex, pageSize, orderBy, order, tableState.filter]
  );

  const { data, isPending, isError } = useQuery({
    queryKey: ["subscription-payment-table", queryInput],
    queryFn: async () => {
      const response = await getAllFiltered<Delivery, keyof Delivery>(
        queryInput,
        "Delivery",
        tableState.deliveriesTableQueryFields!
      );
      return response.data;
    },
    enabled: Boolean(tableState.deliveriesTableQueryFields),
  });

  const pageCount = Math.ceil((data?.total ?? 0) / pageSize);

  const handlePaginationChange = (pagination: PaginationState) => {
    const nextParams = new URLSearchParams(searchParams);

    if (pagination.pageIndex > 0) {
      nextParams.set("page", `${pagination.pageIndex}`);
    } else {
      nextParams.delete("page");
    }

    if (pagination.pageSize !== 10) {
      nextParams.set("pageSize", String(pagination.pageSize));
    } else {
      nextParams.delete("pageSize");
    }

    setSearchParams(nextParams);
  };

  const handleSortingChange = (sorting: Array<{ id: string; desc: boolean }>) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", nextSort.id);
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };

  const { table } = useDataTable<Delivery>({
    data: data?.records ?? [],
    columns: SUBSCRIPTION_PAYMENT_COLUMNS,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    externalPagination: { pageIndex, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
  });

  return (
    <Box sx={{ p: 2 }}>
      {Boolean(tableState.deliveriesTableQueryFields) && (
        <Stack gap={2}>
          <DataTable isLoading={isPending} table={table} totalCount={data?.total ?? 0} />

          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            {isPending ? <Typography variant="body2">Loading...</Typography> : null}
            {isError ? (
              <Typography color="error" variant="body2">
                Failed to load invoices.
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

export default SubscriptionInvoicesTable;
